import React, {useState} from "react";
import {
    Box,
    Collapse,
    Divider,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {useTranslation} from "react-i18next";
import {variableChipSx} from "../../../common/variables/variableChipStyles";
import type {PrintPreviewVariableRow} from "./printoutDocumentVariables";

interface Props {
    rows: PrintPreviewVariableRow[];
}

// Screen-only check that distinguishes resolved values from printed fallbacks.
const PrintPreviewVariablesSection = ({rows}: Props) => {
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState(false);

    if (!rows.length) return null;

    const unresolvedCount = rows.filter(row => row.unresolved).length;

    return (
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 1,
                boxShadow: "none",
                overflow: "hidden",
            }}
        >
            <Stack
                alignItems="center"
                direction="row"
                onClick={() => setExpanded(current => !current)}
                spacing={1}
                sx={{
                    cursor: "pointer",
                    px: 1.75,
                    py: 1.25,
                    userSelect: "none",
                    "&:hover": {
                        bgcolor: "action.hover",
                    },
                }}
            >
                <ExpandMoreIcon
                    sx={{
                        color: "text.secondary",
                        fontSize: 20,
                        transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                        transition: "transform 150ms",
                    }}
                />
                <Typography sx={{color: "text.secondary", fontSize: 13.5}}>
                    {t("Variables")}
                </Typography>
                <Typography sx={{color: "text.disabled", fontSize: 13.5}}>
                    {rows.length}
                </Typography>

                <Box flexGrow={1}/>

                {unresolvedCount > 0 && (
                    <Stack alignItems="center" direction="row" spacing={0.5}>
                        <WarningAmberIcon sx={{color: "warning.main", fontSize: 18}}/>
                        <Typography sx={{color: "warning.main", fontSize: 13.5, fontWeight: 600}}>
                            {unresolvedCount}
                        </Typography>
                    </Stack>
                )}
            </Stack>

            <Collapse in={expanded} unmountOnExit>
                <Divider/>

                <Stack divider={<Divider/>}>
                    {rows.map(row => (
                        <Stack
                            alignItems="baseline"
                            direction="row"
                            key={row.reference}
                            spacing={1.5}
                            sx={{
                                px: 1.75,
                                py: 1.25,
                            }}
                        >
                            <Tooltip placement="left" title={row.label}>
                                <Box
                                    component="span"
                                    sx={{
                                        ...variableChipSx,
                                        flexShrink: 0,
                                        fontSize: 12,
                                        maxWidth: 168,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {row.reference}
                                </Box>
                            </Tooltip>

                            {row.unresolved ? (
                                <Tooltip title={t("This variable has no value for this record")}>
                                    <Stack
                                        alignItems="center"
                                        direction="row"
                                        spacing={0.5}
                                        sx={{minWidth: 0}}
                                    >
                                        <WarningAmberIcon
                                            sx={{
                                                color: "warning.main",
                                                flexShrink: 0,
                                                fontSize: 16,
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                color: "warning.main",
                                                fontSize: 14,
                                                overflowWrap: "anywhere",
                                            }}
                                        >
                                            {/* Quote fallback values so empty output remains distinguishable. */}
                                            {row.value ? `"${row.value}"` : t("empty")}
                                        </Typography>
                                    </Stack>
                                </Tooltip>
                            ) : (
                                <Typography
                                    sx={{
                                        color: "text.primary",
                                        fontSize: 14,
                                        fontWeight: 600,
                                        overflowWrap: "anywhere",
                                    }}
                                >
                                    {row.value}
                                </Typography>
                            )}
                        </Stack>
                    ))}
                </Stack>
            </Collapse>
        </Paper>
    );
};

export default PrintPreviewVariablesSection;
